import { lockOrientation } from '../telegram';

/**
 * Jogo sempre na horizontal no celular.
 *
 * O Telegram só deixa travar a orientação em que o aparelho já está, então:
 * - celular deitado: layout de paisagem e trava (lockOrientation);
 * - celular em pé: o próprio app gira o jogo 90° (classe `rotated`), e a pessoa só vira o
 *   aparelho. Para digitar no chat, o giro sai (o teclado do celular vem em pé).
 *
 * Fora do jogo (tela inicial, cadastro) fica normal, em pé. No computador não muda nada.
 */
export function setupOrientation(game: HTMLElement): { update: () => void } {
  const root = document.documentElement;
  const body = document.body;
  const touch = matchMedia('(pointer: coarse)');

  const update = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const portrait = height > width;
    const inGame = !game.hidden;
    const typing = body.classList.contains('typing');
    const rotate = touch.matches && inGame && portrait && !typing;

    body.classList.toggle('rotated', rotate);
    body.classList.toggle('landscape', touch.matches && (!portrait || rotate));
    root.style.setProperty('--vw', `${width}px`);
    root.style.setProperty('--vh', `${height}px`);
    root.style.setProperty('--game-w', `${rotate ? height : width}px`);
    root.style.setProperty('--game-h', `${rotate ? width : height}px`);

    if (touch.matches && inGame && !portrait) lockOrientation();
  };

  // Campo de texto com foco dentro do jogo (chat): sai do giro enquanto digita.
  const isTextField = (target: EventTarget | null) =>
    target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
  document.addEventListener('focusin', (event) => {
    if (isTextField(event.target) && game.contains(event.target as Node)) {
      body.classList.add('typing');
      update();
    }
  });
  document.addEventListener('focusout', (event) => {
    if (isTextField(event.target)) {
      body.classList.remove('typing');
      // Espera o teclado fechar antes de girar de novo.
      setTimeout(update, 250);
    }
  });

  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);
  touch.addEventListener('change', update);
  new MutationObserver(update).observe(game, { attributes: true, attributeFilter: ['hidden'] });
  update();
  return { update };
}

/** O jogo está girado pelo app? (os controles de toque precisam saber) */
export const isRotated = (): boolean => document.body.classList.contains('rotated');
